import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import staticPosts from '../data/blogPosts';

/* Blog posts come from two places and are merged by slug.
 
   The posts in src/data/blogPosts.js remain the seed set: they ship in the
   bundle, prerender into static HTML, and keep working if Firestore is
   unreachable. Posts written in the admin console live in the blogPosts
   collection and win on a slug collision, so an administrator can edit or
   replace a seeded post without a deploy.
 
   Only published posts are ever served to the public. Drafts are visible in
   the admin console alone. */

const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

function normalize(docSnap) {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    slug: d.slug || docSnap.id,
    title: d.title || 'Untitled',
    author: d.author || 'John C. Ferguson, MD, FACS',
    date: d.date || '',
    category: d.category || 'Insights',
    categoryColor: d.categoryColor || 'accent-indigo',
    description: d.description || '',
    readTime: d.readTime || '',
    featured: Boolean(d.featured),
    heroImage: d.heroImage || '',
    content: d.content || '',
    status: d.status || 'published',
    source: 'firestore',
  };
}

function mergeBySlug(remote) {
  const bySlug = new Map();
  staticPosts.forEach((p) => bySlug.set(p.slug, { ...p, source: 'static', status: 'published' }));
  remote.forEach((p) => bySlug.set(p.slug, p));
  return [...bySlug.values()].sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/* Live published posts for the public blog pages. */
export function usePosts() {
  const [posts, setPosts] = useState(() => mergeBySlug([]));

  useEffect(() => {
    if (isPrerender) return undefined;
    let active = true;
    const q = query(collection(db, 'blogPosts'), where('status', '==', 'published'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        if (!active) return;
        setPosts(mergeBySlug(snap.docs.map(normalize)));
      },
      () => {
        // Keep the seeded posts on any read failure.
      }
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return posts;
}

export function usePost(slug) {
  const posts = usePosts();
  return posts.find((p) => p.slug === slug) || null;
}

/* Every post including drafts, for the admin console only. */
export function useAllPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'blogPosts'),
      (snap) => {
        setPosts(snap.docs.map(normalize).sort((a, b) => String(b.date).localeCompare(String(a.date))));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsubscribe;
  }, []);

  return { posts, loading, staticPosts };
}

export function slugify(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}
